import './slideout.scss';

import Button, { ButtonState, ButtonType } from '../button/Button';
import Icon, { IconType } from '../icon/Icon';
import { Tooltip, TooltipPlacement } from '../tooltip/tooltip';
import { useEffect, useState } from 'react';

import { hookIsMobile } from '../../lib/system-selector/hooks/hookIsMobile';

export type BaseSlideoutProps = {
  isVisible: boolean;
  title?: string;
  className?: string;
  position?: 'left' | 'right';
  errorMessage?: string;
  isLoading?: boolean;
  smartListerLabels?: any;
  onClose?: () => void;
  children: React.ReactNode;
};

const BaseSlideout = ({
  isVisible,
  title,
  className = '',
  position = 'right',
  errorMessage,
  isLoading = false,
  smartListerLabels = {},
  onClose,
  children,
}: BaseSlideoutProps) => {
  const [shouldRender, setShouldRender] = useState(isVisible);

  const isMobile = hookIsMobile('--from-desktop');

  useEffect(() => {
    if (isVisible) {
      setShouldRender(true);
      return;
    } else {
      setShouldRender(false);
    }
  }, [isVisible]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isVisible) {
        onClose?.();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isVisible, onClose]);

  if (!shouldRender) return null;

  return (
    <div className="slideout-overlay">
      <div className="slideout-background" aria-hidden="true" />
      <div
        className={`slideout-panel ${position} ${
          isVisible && (isMobile ? 'slide-in-up' : `slide-in ${position}`)
        } ${className}`}
      >
        <div className="slideout-content">
          <div className="slideout-header" style={{ marginBottom: '10px' }}>
            {errorMessage ? (
              <span />
            ) : isLoading ? (
              <div className="skeleton-title" />
            ) : (
              <h4 className="slideout-title">{title}</h4>
            )}
            <Tooltip
              content={smartListerLabels?.closeButtonHoverText || 'Close panel'}
              placement={TooltipPlacement.BOTTOM}
              zIndex={10000}
            >
              <Button
                type={ButtonType.TERTIARY}
                state={ButtonState.DEFAULT}
                extraClasses="slideout-close"
                onClick={onClose}
                aria-label="Close panel"
                icon={IconType.Close}
              />
            </Tooltip>
          </div>

          {errorMessage ? (
            <>
              <div className="error-state">
                <Icon
                  iconType={IconType.ErrorQuestion}
                  className="u-icon-display"
                />
                <p className="error-message">
                  <strong>Something went wrong</strong>
                  <br />
                  We couldn’t load the content for this.
                  <br />
                  Please try again or refresh the page.
                </p>
              </div>
            </>
          ) : (
            children
          )}
        </div>
      </div>
    </div>
  );
};

export default BaseSlideout;
